import { trigger, transition, style, animate } from '@angular/animations';
import {
  Component,
  OnInit,
  ChangeDetectorRef,
  ElementRef,
  HostListener,
  OnDestroy,
  PLATFORM_ID,
  inject,
  input,
  output,
  viewChild,
} from '@angular/core';
import { fromEvent, Subject } from 'rxjs';
import { debounceTime, takeUntil } from 'rxjs/operators';
import { ContentPage } from '../../../content/models';
import * as fromStore from '../../../disease-profiles/store';
import { Store } from '@ngrx/store';
import {
  GetMagazineSearchResults,
  getMagazineSearchResultsLoading,
  getRankedMagazineSearchResults,
  getRankedSearchResults,
  GetSearchResults,
  getSearchResultsLoading,
} from '../../../search/store';
import { LoadDiseaseProfiles } from '../../../disease-profiles/store';
import { isPlatformBrowser } from '@angular/common';
import { RouterLink, RouterLinkActive } from '@angular/router';
import { IconComponent } from '@teamfoster/sdk/icon';
import { SearchFieldComponent } from '../search-field/search-field.component';
import { SearchResultListComponent } from '../search-result-list/search-result-list.component';
import { FromDictionaryPipe } from '@teamfoster/sdk/dictionary-ngrx';
@Component({
  selector: 'app-topbar',
  templateUrl: './topbar.component.html',
  animations: [
    trigger('inOutAnimation', [
      transition(':enter', [style({ opacity: 0 }), animate('.1s ease-out', style({ opacity: 1 }))]),
      transition(':leave', [style({ opacity: 1 }), animate('.1s ease-in', style({ opacity: 0 }))]),
    ]),
  ],
  imports: [RouterLink, IconComponent, SearchFieldComponent, SearchResultListComponent, RouterLinkActive, FromDictionaryPipe],
})
export class TopbarComponent implements OnInit, OnDestroy {
  private cd = inject(ChangeDetectorRef);
  private store = inject(Store);
  private platformId = inject(PLATFORM_ID);

  readonly focusStart = viewChild<ElementRef<HTMLElement>>('focusStart');

  readonly loading = input<boolean>();
  readonly menuActive = input<boolean>();
  readonly logoClass = input<string>();
  readonly menuItems = input<ContentPage[]>();

  readonly menuToggle = output<void>();
  readonly menuClose = output<void>();
  readonly menuOpen = output<void>();

  fixed = false;

  staticMenuItems = [
    {
      menuTitle: 'Home',
      title: '',
      url: ['/'],
      order: 10,
      pathMatchExact: true,
    },
    {
      menuTitle: 'Stofwisselingsziekten',
      title: '',
      url: ['/', 'stofwisselingsziekten'],
      order: 20,
      pathMatchExact: true,
    },
    {
      menuTitle: 'Nieuws',
      title: '',
      url: ['/', 'nieuws'],
      order: 30,
      pathMatchExact: true,
    },
    {
      menuTitle: 'Evenementen',
      title: '',
      url: ['/', 'evenementen'],
      order: 40,
      pathMatchExact: true,
    },
    {
      menuTitle: 'Blogs',
      title: '',
      url: ['/', 'blogs'],
      order: 50,
      pathMatchExact: true,
    },
  ];

  profiles$ = this.store.selectSignal(fromStore.getAllOrderedDiseaseProfiles);
  searchResults$ = this.store.selectSignal(getRankedSearchResults);
  resultsLoading$ = this.store.selectSignal(getSearchResultsLoading);
  magazineSearchResults$ = this.store.selectSignal(getRankedMagazineSearchResults);
  magazineResultsLoading$ = this.store.selectSignal(getMagazineSearchResultsLoading);

  q: string | null = null;

  setSearchFocus = false;

  private _unsubscribe = new Subject<void>();

  ngOnInit() {
    if (isPlatformBrowser(this.platformId)) {
      fromEvent(window, 'scroll')
        .pipe(debounceTime(10), takeUntil(this._unsubscribe))
        // tslint:disable-next-line: deprecation
        .subscribe(a => {
          // show scrollbar after windowinnerheight (window.innerHeight)
          if (window.scrollY > 10) {
            this.fixed = true;
          } else {
            this.fixed = false;
          }
          this.cd.detectChanges();
        });
    }
  }

  handleSearchInput(q: string) {
    this.q = q;
    this.store.dispatch(GetSearchResults({ q }));
    this.store.dispatch(GetMagazineSearchResults({ q }));
    this.store.dispatch(LoadDiseaseProfiles({ skip: 0, take: 51, q: q }));
  }

  get allMenuItems() {
    const menuItems = this.menuItems();
    if (menuItems?.length) {
      return [...this.staticMenuItems, ...menuItems].sort((a, b) => (a.order > b.order ? 1 : -1));
    } else {
      return this.staticMenuItems.sort((a, b) => (a.order > b.order ? 1 : -1));
    }
  }

  lastFocusable() {
    this.focusStart()?.nativeElement.focus();
  }

  openMenu(focusSearch: boolean) {
    this.menuToggle.emit();
    this.q = '';

    if (focusSearch) {
      this.setSearchFocus = true;
    }
  }

  @HostListener('document:keyup.escape', ['$event']) onKeydownHandler(event: Event) {
    this.menuClose.emit();
  }

  handleFocused(e: boolean) {
    if (e) {
      this.setSearchFocus = false;
    }
  }

  closeNav() {
    this.q = null;
    this.menuClose.emit();
  }

  ngOnDestroy(): void {
    this._unsubscribe.next();
    this._unsubscribe.complete();
  }
}
